// Visit options for the settle sheet's copay declaration (GDN-2405).
//
// Wraps the existing encounters list endpoint (the same
// `useListEncountersQuery` the encounters feature exposes) scoped to the
// patient, newest first. Degrades gracefully: while the list is loading —
// or if it errors / doesn't include the drawer's context encounter — the
// context encounter still appears as a bare "This visit" option so the
// declaration is never blocked on the list fetch.

import { useMemo } from 'react';
// Direct file import (no feature barrel) — avoids the encounters feature's
// auto-registration chain; the RTK endpoints are injected into baseApi.
import { useListEncountersQuery } from '@features/encounters/store/api';

export interface CopayVisitOption {
  id: string;
  /** Resolved service date (YYYY-MM-DD…), when known. */
  serviceDate: string | null;
  /** Encounter or appointment start time, when known. */
  startsAt?: string | null;
  /** Human-readable visit label, e.g. "Therapy Session". */
  label: string;
  /** First CPT code on the encounter, when coded. */
  cptCode: string | null;
  /** True for the drawer's context encounter (preselected). */
  isContext: boolean;
}

interface UseRecentEncounterOptionsArgs {
  patientId: string;
  /** The drawer's in-scope encounter — always listed first when present. */
  contextEncounterId?: string | null;
  /** Eligibility-pinned appointment time for labeling a missing context encounter. */
  contextVisitStartsAt?: string | null;
  /** Skip the list fetch entirely (e.g. while the copay mode is hidden). */
  skip?: boolean;
}

interface UseRecentEncounterOptions {
  visits: CopayVisitOption[];
  isLoading: boolean;
}

const RECENT_VISIT_COUNT = 5;

export function useRecentEncounterOptions({
  patientId,
  contextEncounterId,
  contextVisitStartsAt,
  skip,
}: UseRecentEncounterOptionsArgs): UseRecentEncounterOptions {
  const { data, isLoading } = useListEncountersQuery(
    {
      patientId,
      // The canonical resolved visit date. 'start_time' used to be sent
      // here and still works, but only because the server aliases it —
      // 16.9% of encounters have no start_time and Postgres DESC is NULLS
      // FIRST, so the raw column filled this five-slot picker with
      // dateless encounters and never reached the patient's real visits
      // (gdn-3102). Asking for what we actually mean is what lets the
      // alias be retired (gdn-3104).
      sortBy: 'service_date',
      sortOrder: 'desc',
      perPage: RECENT_VISIT_COUNT,
    },
    { skip: !!skip || !patientId },
  );

  return useMemo(() => {
    const options: CopayVisitOption[] = (data?.items ?? []).map(encounter => ({
      id: encounter.encounter_id,
      serviceDate: encounter.service_date ?? null,
      startsAt: encounter.start_time ?? null,
      label: humanizeEncounterType(encounter.encounter_type),
      cptCode: encounter.cpt_codes?.[0] ?? null,
      isContext: encounter.encounter_id === contextEncounterId,
    }));

    // Context encounter first — it's what the toolbar/drawer scoped to.
    const context = options.filter(option => option.isContext);
    const rest = options.filter(option => !option.isContext);
    const visits = [...context, ...rest];

    if (contextEncounterId && context.length === 0) {
      // List still loading (or missing the context visit) — degrade to a
      // bare option so the declaration can proceed against the context.
      visits.unshift({
        id: contextEncounterId,
        serviceDate: contextVisitStartsAt?.slice(0, 10) ?? null,
        startsAt: contextVisitStartsAt ?? null,
        label: 'Appointment',
        cptCode: null,
        isContext: true,
      });
    }

    return { visits, isLoading };
  }, [contextEncounterId, contextVisitStartsAt, data, isLoading]);
}

/** "therapy_session" → "Therapy session"; passthrough for humane labels. */
function humanizeEncounterType(encounterType?: string | null): string {
  const raw = encounterType?.trim();
  if (!raw) return 'Visit';
  const spaced = raw.replace(/[_-]+/g, ' ');
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

// ── Display helpers for CopayVisitOption ────────────────────────────────────
// Here rather than in a component file so both the copay declaration and
// the charge-attribution picker (gdn-3086) can import them without tripping
// react-refresh's only-export-components rule.

/** "Jul 6 · Therapy session · CPT 90834" — degrades to whatever we know. */
export const visitDescriptor = (visit: CopayVisitOption): string =>
  [
    visitDateTime(visit),
    visit.label,
    visit.cptCode ? `CPT ${visit.cptCode}` : null,
  ]
    .filter(Boolean)
    .join(' · ') || 'this visit';

export const visitDateTime = (visit: CopayVisitOption): string | null => {
  if (!visit.startsAt) {
    return visit.serviceDate ? shortVisitDate(visit.serviceDate) : null;
  }
  const parsed = new Date(visit.startsAt);
  if (Number.isNaN(parsed.getTime())) return visit.startsAt;
  const date = parsed.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
  });
  const time = parsed.toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit',
  });
  return `${date}, ${time}`;
};

const shortVisitDate = (date: string): string => {
  const parsed = new Date(`${date.slice(0, 10)}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return date;
  return parsed.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};
